import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastAlert } from "@paylixecommerce/shared/components";
import {
  initThemeCacheAction,
  initThemeTemplateCacheAction,
  updateThemeCacheAction,
  deleteThemeCacheAction,
} from './actions';
import { ChangesHistoryContext } from './ChangeHistoryContext';
import { useThemesContext } from '../../../context/ThemeProvider';
import { getIframeWindow } from '../hooks/useIFrameEvents';

// Enhanced function to safely send postMessage with retry
const safePostMessage = (message, retries = 3) => {
  const attemptSend = (attempt) => {
    const iframeWindow = getIframeWindow();
    
    if (iframeWindow) {
      try {
        iframeWindow.postMessage(message, '*');
        console.log('PostMessage sent successfully:', message.eventName);
        return true;
      } catch (error) {
        console.error('PostMessage failed:', error);
      }
    }
    
    if (attempt < retries) {
      console.warn(`PostMessage attempt ${attempt} failed, retrying...`);
      setTimeout(() => attemptSend(attempt + 1), 100);
    } else {
      console.error('PostMessage failed after all attempts');
    }
    return false;
  };
  
  attemptSend(1);
};

export const getHistoryPath = (
  isSystem,
  layoutKey,
  templateKey,
  sectionKey,
) => ({
  isSystem,
  layoutKey,
  templateKey,
  sectionKey,
});

export function ChangesHistoryProvider({ children, currentTheme }) {
  const dispatch = useDispatch();

  const { id: shopId, name: shopName } = useSelector(
    ({ auth }) => auth?.shop || {},
  );

  const { selectedTheme, selectedTemplate } = useThemesContext();

  const initialState = {
    historyPath: null,
    layouts: currentTheme?.layouts,
    templates: currentTheme?.templates,
    settings: currentTheme?.settings,
  };
  const [history, setHistory] = useState([initialState]);
  const [currentStateIndex, setCurrentStateIndex] = useState(0);
  const [themeCache, setThemeCache] = useState(null);
  const [pageSlug, setPageSlug] = useState('/');

  const defaultLayout = useMemo(() =>
    Object.values(history[currentStateIndex]?.layouts)?.find(({ is_default }) => !!is_default),
[history, currentStateIndex]
  );

  const themeCacheId = themeCache?.cache_id;

  const renderTemplate = (layoutId, templateId, settings) => {
    const message = {
      eventName: 'PaylixBuilderRenderTemplateEvent',
      data: {
        themeId: selectedTheme?.id,
        layoutId,
        templateId,
        settings,
      },
    };
    safePostMessage(message);
  };

  const renderSection = (layoutId, templateId, sectionName, settings) => {
    const message = {
      eventName: 'PaylixBuilderRenderSectionEvent',
      data: {
        themeId: selectedTheme?.id,
        layoutId,
        templateId,
        sectionName,
        settings,
      },
    };
    safePostMessage(message);
  };

  const reloadIframe = () => {
    const message = {
      eventName: 'PaylixBuilderThemeChangeEvent',
      data: {},
    };
    safePostMessage(message);
  };

  const initThemeCache = useCallback(
    async (theme, layout, template) => {
      const body = {
        shop_id: shopId,
        theme_info: {
          id: theme.id,
          settings: theme.settings,
        },
        layout_info: {
          name: layout.name,
          settings: layout.settings,
        },
        template_info: {
          name: template.name,
          settings: template.settings,
        },
      };

      try {
        const { data } = await dispatch(
          initThemeCacheAction({ shopName, body }),
        );

        setThemeCache(data);
      } catch (error) {
        return toastAlert('error', 'Something went wrong');
      }
    },
    [dispatch],
  );

  const initThemeTemplateCache = useCallback(
    async (cachedData, template) => {
      const {
        cache: { renderOptions },
        cache_id: cacheId,
      } = cachedData;

      const cachedLayout = renderOptions.layouts[defaultLayout?.name];
      const cachedThemeSettings = renderOptions.theme.settings;

      const body = {
        shop_id: shopId,
        theme_info: {
          id: selectedTheme?.id,
          settings: cachedThemeSettings ?? selectedTheme?.settings,
        },
        layout_info: {
          name: cachedLayout?.name ?? defaultLayout?.name,
          settings: cachedLayout?.settings ?? defaultLayout?.settings,
        },
        template_info: {
          name: template.name,
          settings: template.settings,
        },
      };

      try {
        const { data } = await dispatch(
          initThemeTemplateCacheAction({
            shopName,
            cacheId,
            body,
          }),
        );

        setThemeCache(data);
      } catch (error) {
        return toastAlert('error', 'Something went wrong');
      }
    },
    [dispatch],
  );

  const updateThemeCache = useCallback(
    async (cacheId, updatedData) => {
      const body = {
        shop_id: shopId,
        ...updatedData,
      };

      try {
        const { data } = await dispatch(
          updateThemeCacheAction({
            shopName,
            cacheId,
            body,
          }),
        );

        setThemeCache(data);
      } catch (error) {
        return toastAlert('error', 'Something went wrong');
      }
    },
    [dispatch],
  );

  const deleteThemeCache = useCallback(
    async cacheId => {
      try {
        await dispatch(
          deleteThemeCacheAction({
            shopName,
            cacheId,
          }),
        );
      } catch (error) {
        return toastAlert('error', 'Something went wrong');
      }
    },
    [dispatch],
  );

  const rerenderOnChangeHistory = (
    currentHistory,
    targetPath,
    selectedTemplateKey,
  ) => {
    const {
      isSystem,
      layoutKey,
      templateKey,
      sectionKey,
      isUpdateThemeSettings,
    } = targetPath;
    let data;

    if (isUpdateThemeSettings) {
      data = {
        theme_info: {
          id: selectedTheme?.id,
          settings: currentHistory?.settings,
        },
      };
    } else {
      const doesRerender = selectedTemplateKey === templateKey;
      const targetSettings = isSystem
        ? currentHistory.layouts[layoutKey].settings.sections[sectionKey]
            ?.settings
        : currentHistory.templates[templateKey].settings;
      const targetEntity = isSystem
        ? currentHistory.layouts[layoutKey]
        : currentHistory.templates[templateKey];
      data = isSystem
        ? {
            layout_info: {
              name: targetEntity.name,
              settings: targetEntity.settings,
            },
          }
        : {
            template_info: {
              name: targetEntity.name,
              settings: targetEntity.settings,
            },
          };

      isSystem
        ? renderSection(layoutKey, templateKey, sectionKey, targetSettings)
        : doesRerender &&
          renderTemplate(layoutKey, templateKey, targetSettings);
    }

    updateThemeCache(themeCacheId, data).then(
      isUpdateThemeSettings && reloadIframe(),
    );
  };

  const onChange = useCallback(
    state => {
      setHistory(prev => {
        const h = prev.concat();
        h.splice(currentStateIndex + 1);
        return [
          ...h,
          typeof state === 'function' ? state(prev[currentStateIndex]) : state,
        ];
      });
      setCurrentStateIndex(currentStateIndex + 1);
    },
    [currentStateIndex],
  );

  const onChangeThemeSettings = useCallback(
    (schemaValuesKey, id, newValue, cacheId) => {
      onChange(prevState => {
        const updatedThemeSettings = {
          ...prevState.settings,
          properties: {
            ...prevState.settings.properties,
            [schemaValuesKey]: {
              ...prevState.settings?.properties?.[schemaValuesKey],
              [id]: newValue,
            },
          },
        };

        const updatedData = {
          theme_info: {
            id: selectedTheme?.id,
            settings: updatedThemeSettings,
          },
        };

        updateThemeCache(cacheId, updatedData).then(reloadIframe());

        return {
          ...prevState,
          historyPath: {
            isUpdateThemeSettings: true,
          },
          settings: updatedThemeSettings,
        };
      });
    },
    [onChange],
  );

  const onChangeSection = useCallback(
    (selectedParent, sectionId, entity, isSystem = false, cacheId) => {
      const parentKey = isSystem ? 'layouts' : 'templates';

      onChange(prevState => {
        const updatedState = {
          ...prevState,
          historyPath: getHistoryPath(
            isSystem,
            defaultLayout?.name,
            selectedTemplate?.name,
            sectionId,
          ),
          [parentKey]: {
            ...prevState[parentKey],
            [selectedParent]: {
              ...prevState?.[parentKey]?.[selectedParent],
              settings: {
                ...prevState?.[parentKey]?.[selectedParent]?.settings,
                sections: {
                  ...prevState?.[parentKey]?.[selectedParent]?.settings
                    ?.sections,
                  [sectionId]: {
                    ...entity,
                  },
                },
              },
            },
          },
        };

        const targetEntity = updatedState[parentKey][selectedParent];
        const data = isSystem
          ? {
              layout_info: {
                name: targetEntity.name,
                settings: targetEntity.settings,
              },
            }
          : {
              template_info: {
                name: targetEntity.name,
                settings: targetEntity.settings,
              },
            };

        updateThemeCache(cacheId, data);

        return updatedState;
      });
    },
    [onChange, defaultLayout, selectedTemplate],
  );

  const onChangeBlock = useCallback(
    (selectedParent, sectionId, blockId, entity, isSystem = false, cacheId) => {
      const parentKey = isSystem ? 'layouts' : 'templates';

      onChange(prevState => {
        const updatedState = {
          ...prevState,
          historyPath: getHistoryPath(
            isSystem,
            defaultLayout?.name,
            selectedTemplate?.name,
            sectionId,
          ),
          [parentKey]: {
            ...prevState[parentKey],
            [selectedParent]: {
              ...prevState?.[parentKey]?.[selectedParent],
              settings: {
                ...prevState?.[parentKey]?.[selectedParent]?.settings,
                sections: {
                  ...prevState?.[parentKey]?.[selectedParent]?.settings
                    ?.sections,
                  [sectionId]: {
                    ...prevState?.[parentKey]?.[selectedParent]?.settings
                      ?.sections?.[sectionId],
                    settings: {
                      ...prevState?.[parentKey]?.[selectedParent]?.settings
                        ?.sections?.[sectionId].settings,
                      blocks: {
                        ...prevState?.[parentKey]?.[selectedParent]?.settings
                          ?.sections?.[sectionId].settings?.blocks,
                        [blockId]: entity,
                      },
                    },
                  },
                },
              },
            },
          },
        };

        const targetEntity = updatedState[parentKey][selectedParent];
        const data = isSystem
          ? {
              layout_info: {
                name: targetEntity.name,
                settings: targetEntity.settings,
              },
            }
          : {
              template_info: {
                name: targetEntity.name,
                settings: targetEntity.settings,
              },
            };

        updateThemeCache(cacheId, data);

        return updatedState;
      });
    },
    [onChange, defaultLayout, selectedTemplate],
  );

  const goHistoryBack = useCallback(() => {
    if (currentStateIndex > 0) {
      setCurrentStateIndex(currentStateIndex - 1);

      const currentHistory = history[currentStateIndex - 1];
      const targetPath = history[currentStateIndex].historyPath;

      rerenderOnChangeHistory(
        currentHistory,
        targetPath,
        selectedTemplate?.name,
      );
    }
  }, [history, currentStateIndex, selectedTemplate]);

  const goHistoryForward = useCallback(() => {
    if (currentStateIndex + 1 < history.length) {
      setCurrentStateIndex(currentStateIndex + 1);

      const currentHistory = history[currentStateIndex + 1];
      const targetPath = currentHistory.historyPath;

      rerenderOnChangeHistory(
        currentHistory,
        targetPath,
        selectedTemplate?.name,
      );
    }
  }, [history, currentStateIndex, selectedTemplate]);

  const clearHistory = useCallback(() => {
    setCurrentStateIndex(0);
    setHistory([history[currentStateIndex]]);
  }, [history, currentStateIndex]);

  const handleIframeNavigate = href => {
    setPageSlug(href);
  };

  useEffect(() => {
    if (!themeCacheId && selectedTheme && defaultLayout && selectedTemplate) {
      initThemeCache(selectedTheme, defaultLayout, selectedTemplate);
    }
  }, [themeCacheId, selectedTheme, defaultLayout, selectedTemplate]);

  const context = {
    // HISTORY
    history,
    onChange,
    onChangeThemeSettings,
    onChangeSection,
    onChangeBlock,
    currentState: history[currentStateIndex],
    currentStateIndex,
    goHistoryForward,
    goHistoryBack,
    clearHistory,

    //THEME_CACHE
    themeCache,
    themeCacheId,
    pageSlug,
    setPageSlug,
    initThemeCache,
    initThemeTemplateCache,
    deleteThemeCache,
    handleIframeNavigate,
    updateThemeCache,
  };

  return (
    <ChangesHistoryContext.Provider value={context}>
      {children}
    </ChangesHistoryContext.Provider>
  );
}
